import { useTags } from '@entities/common';
import { useGroups } from '@entities/common/groups';
import { useSaveData } from '@entities/common/savedData';
import { useContacts } from '@entities/contacts';
import { useGetEventByIdQuery } from '@entities/events/rtk-query';
import { promoApi } from '@entities/promo-video';
import { yupResolver } from '@hookform/resolvers/yup';
import axios, { type AxiosProgressEvent } from 'axios';
import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { xhr } from '@shared/config';
import { useModuleByCodeParam } from '@shared/hooks/use-module-by-code-param.ts';
import type { Group } from '@shared/types/common';
import type { PromoVideo } from '@shared/types/promo-videos';
import { useAppDispatch } from '@src/library/hooks/common';
import useWindowDimensions from '@src/library/hooks/common/useWindowDimensions';
import type { Event } from '@src/library/models/events';
import { eventService } from 'library/api/eventService';
import { defaultEditorValues, normalizeEvent, resetEditor, schema } from 'library/helpers/events';
import { useEvent } from 'library/hooks/events';
import { addEventToList, changeEvent, patchEvent } from 'library/redux/events';
import { openDialogModal, openNotifyModal, showPopup } from 'library/redux/modal';
import type { FormEditor } from 'library/types/events';

interface PromoCreateRes {
  id: number;
  progress_bar_url: string;
  upload_url: string;
}

export const useEventEditor = () => {
  const { id } = useParams();
  const [progress, setProgress] = useState<AxiosProgressEvent>();
  const navigate = useNavigate();
  const { state } = useLocation();
  const eventId = state?.copyEventId ?? Number(id);
  const dispatch = useAppDispatch();

  const {
    data: savedData,
    doSetStorageData,
    doRemoveStorageData,
  } = useSaveData<FormEditor>('EventForm');

  const { setSelectedContacts } = useContacts();
  const { module, code } = useModuleByCodeParam();
  // const event = useEvent();
  const { data: event, isLoading } = useGetEventByIdQuery(eventId, { skip: eventId === undefined });

  const { sendEvent } = usePublicate();
  const [editorStatus, setEditorStatus] = React.useState<string>('idle');
  const { width } = useWindowDimensions();
  const showCloseBtn = width <= 1024;
  const defaultFormValues = savedData || defaultEditorValues;

  const methods = useForm<FormEditor>({
    resolver: yupResolver(schema as any),
    defaultValues: { ...defaultFormValues },
    reValidateMode: 'onChange',
  });

  const mode = methods.watch('mode');

  const hasDirtyFields = () => {
    const {
      formState: { errors },
    } = methods;
    return Object.keys(errors).length === 0;
  };

  const { selectedTags, resetSelectedTags } = useTags();
  const { resetSelectedGroups, groups, initialGroups } = useGroups();

  const onClose = () => {
    resetSelectedGroups();
    // resetSelectedModule();
    resetSelectedTags();
    navigate(`/events/${code}`);
  };

  const publish = (id: number) => {
    // const published = true;
    // sendEvent(id, published).then(() => {
    navigate(`/events/${code}/${id}`);
    // });
  };

  const instance = axios.create({
    withCredentials: false,
    headers: { 'Content-Type': 'multipart/form-data' },
    onUploadProgress: (progressEvent: AxiosProgressEvent) => {
      setProgress(progressEvent);
    },
  });

  const onSubmit = async (data: FormEditor) => {
    const costsGroup = data.costsEditor?.map((costEdit) => ({
      cost: Number(costEdit.cost) * 100,
      group: costEdit.entityId ?? costEdit.group,
      payment_method: costEdit.payment_method,
    }));
    let event = await normalizeEvent(data);
    // const eventTags = data.tags ? normalizeTags(tags, data.tags as number[]) : [];
    event = { ...event, costs: costsGroup ?? data.costs };

    try {
      if (data.video_file || data.url) {
        setEditorStatus('videoLoading');

        const promoData = new FormData();

        //заглушка для обложки видео
        const prevFile = new Blob(['prev']);
        promoData.set('preview', prevFile);

        const promoConfig = { params: { module: module?.id } };

        const res = await xhr.post<PromoCreateRes>(promoApi, promoData, promoConfig);
        if (!res.data) throw res;

        const formData = new FormData();
        formData.append('video_type', data.video_type);
        if (data.video_type === 'B') {
          formData.append('file', data.video_file);
          await instance.post(res.data.upload_url, formData);
          const checkRes = await xhr.get<PromoVideo>(`${promoApi}${res.data.id}/check/`);
          if (!checkRes.data) throw checkRes;
        } else {
          formData.append('embed_url', data.url);
          const promoPatchRes = await xhr.patch(`${promoApi}/${res.data.id}/`, formData);
          if (!promoPatchRes.data) throw promoPatchRes;
        }

        event = { ...event, presentation_video: res.data.id as number };
      }

      setEditorStatus('loading');

      let response: Event | any;
      if (id) {
        response = await eventService.updateEvent(id, event);
        dispatch(changeEvent(response.data));
      } else {
        response = await eventService.createEvent(event);
        dispatch(addEventToList(response.data));
      }
      if (!response.data) {
        throw response;
      }

      dispatch(
        openDialogModal({
          title: 'Публикация',
          text: 'Опубликовать сейчас или сохранить без публикации?',
          confirmText: 'Сохранить',
          declineText: 'Опубликовать',
          confirm: () => {
            publish(id ? id : response.data.id);
            dispatch(
              showPopup({
                type: 'success',
                text: `Видеопрограмма успешно ${isNaN(id as any) ? 'создана' : 'обновлена'}`,
              })
            );
          },
          decline: () => {
            const eventId = id ?? response.data.id;
            navigate(`/events/event/${eventId}/publication`);
          },
        })
      );
    } catch (error) {
      throw error;
    } finally {
      setEditorStatus('loaded');
    }
    resetSelectedGroups();
    // resetSelectedModule();
    resetSelectedTags();
    setSelectedContacts([]);
    doRemoveStorageData();
  };

  const initSelectedGroups = (event: Event) => {
    const { costs } = event;
    const selectedGroupsToAdd = costs
      .map((cost: any) => {
        return groups.find((group) => group.id === cost.id);
      })
      .filter(Boolean);

    initialGroups(selectedGroupsToAdd as any);
  };

  // const getParsedCosts = (costs: any) => {
  //   return costs
  //     ? costs.map((cost: any) => ({
  //         group: cost.group,
  //         cost: cost.cost,
  //         payment_method: cost.payment_method,
  //       }))
  //     : [];
  // };

  useEffect(() => {
    if (eventId && !isLoading && event) {
      const resetEvent: any = { ...event };
      resetEvent.cost = event.cost / 100;
      resetEvent.costs = event.costs;
      resetEvent.tags = selectedTags.length ? selectedTags : event.tags;
      if (state?.copyEventId) {
        resetEvent.event_image = '';
      }
      methods.reset(resetEditor(resetEvent));
    }
  }, [isLoading]);

  useEffect(() => {
    if (event) {
      initSelectedGroups(event);
    }
  }, [event]);

  return {
    isLoading,
    methods,
    onSubmit,
    event,
    editorStatus,
    mode,
    showCloseBtn,
    onClose,
    progress,
    hasDirtyFields,
    doSetStorageData,
    doRemoveStorageData,
  };
};

export const usePublicate = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { event } = useEvent();

  const updateEvent = (id: number, changes: Partial<Event>) => {
    dispatch(patchEvent({ id: id, data: changes }));
  };
  const sendEvent = async (id: number, published: boolean) => {
    try {
      updateEvent(id, { published });
      const response = await eventService.publicateEvent(id, published);
      dispatch(changeEvent(response.data));
      if (!response.data) {
        throw response;
      }

      if (published) {
        dispatch(
          openNotifyModal({
            title: 'Модерация',
            text: 'Мероприятие отправлено на модерацию',
            confirmText: 'Ок',
          })
        );
      }
    } catch (error) {
      console.error(error);
    } finally {
      navigate(-1);
    }
  };

  const publicate = (id: number) => {
    const published = true;
    dispatch(
      openDialogModal({
        title: 'Публикация',
        text: 'Мероприятие будет отправлено на модерацию. Продолжить?',
        confirmText: 'Да',
        confirm: () => sendEvent(id, published),
        declineText: 'Нет',
        link: {
          url: '',
          text: 'Подробнее о публикации',
        },
      })
    );
  };

  const unpublicate = (id: number) => {
    const published = false;
    //Отправить пустые группы
    dispatch(
      openDialogModal({
        title: 'Снятие с публикации',
        text: 'Вы действительно хотите снять мероприятие с публикации?',
        confirmText: 'Да',
        confirm: () => sendEvent(id, published),
        declineText: 'Нет',
      })
    );
  };

  const changeGroups = async (
    id: number,
    custom_groups: Group[],
    excluded_custom_groups: Group[]
  ) => {
    if (!event) return;
    const arrayIdsGroups: number[] = custom_groups.map((custom_group) => custom_group.id);
    const arrayIdsExcludedGroups: number[] = excluded_custom_groups?.map(
      (excluded_custom_group) => excluded_custom_group.id
    );

    try {
      updateEvent(id, {
        custom_groups: custom_groups,
        excluded_custom_groups: excluded_custom_groups,
      });

      event.published &&
        dispatch(
          openNotifyModal({
            needClose: false,
            title: 'Публикация',
            text: 'Условия публикации успешно изменены',
            confirmText: 'Ок',
          })
        );
      const data = {
        custom_groups: arrayIdsGroups,
        excluded_custom_groups: arrayIdsExcludedGroups,
      };
      return await eventService.updateEvent(id, data);
    } catch (error) {
      console.error(error);
    } finally {
      navigate(-1);
    }
  };

  const publishDialog = () => {
    if (!event) return;
    const id = event.id;

    dispatch(
      openDialogModal({
        title: '',
        text: 'Вы действительно хотите опубликовать видеопрограмму?',
        confirmText: 'Опубликовать',
        confirm: () => publicate(id),
        declineText: 'Отмена',
      })
    );
  };

  const unpublishDialog = () => {
    if (!event || !event.published) return;
    const id = event.id;

    dispatch(
      openDialogModal({
        title: '',
        text: 'Вы действительно хотите снять видеопрограмму с публикации?',
        confirmText: 'Снять',
        confirm: () => unpublicate(id),
        declineText: 'Отмена',
      })
    );
  };

  const publishAction = () => {
    if (!event) return;
    if (!event.published) {
      return {
        title: 'Опубликовать',
        action: () => publishDialog(),
      };
    } else {
      return {
        title: 'Снять с публикации',
        action: () => unpublishDialog(),
      };
    }
  };

  return {
    publicate,
    unpublicate,
    sendEvent,
    changeGroups,
    publishAction: publishAction(),
  };
};
